class RequestQuoteSection extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.request-quote');
    if (!this.section) return;

    this.formId = this.section.dataset.formId;
    this.formHost = this.section.querySelector('[data-request-quote-form]');
    if (!this.formId || !this.formHost) return;

    this.ensureFormStyles();
    this.bindFormHostClicks();
    this.waitForForm(() => {
      this.bindFormEvents();
      this.ensureFormStyles();
      this.syncSuccessState();
    });
    this.observeSuccess();
  }

  getForm() {
    return this.section.querySelector(`form[data-cf-form="${this.formId}"]`);
  }

  getFormInner() {
    return this.formHost.querySelector('.cf-form-inner');
  }

  isSuccessStep() {
    return this.getFormInner()?.dataset.cfOnSuccessStep === 'true';
  }

  ensureFormStyles() {
    const cssUrl = this.formHost.dataset.rqFormCss;
    if (!cssUrl) return;

    const lateId = 'request-quote-form-css-late';
    if (!document.getElementById(lateId)) {
      const link = document.createElement('link');
      link.id = lateId;
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.body.appendChild(link);
    }

    this.formHost.classList.add('request-quote__form-host--styled');
  }

  waitForForm(callback) {
    const run = () => {
      const form = this.getForm();
      if (!form?.querySelector('.cf-form-inner')) return;
      callback(form);
      this.formMountObserver?.disconnect();
    };

    if (typeof window.CF?.ready === 'function') {
      window.CF.ready(run);
    } else {
      document.addEventListener('cf:ready', run, { once: true });
    }

    this.formMountObserver = new MutationObserver(run);
    this.formMountObserver.observe(this.formHost, { childList: true, subtree: true });
    run();
  }

  bindFormEvents() {
    const form = this.getForm();
    if (!form || form.dataset.requestQuoteBound === 'true') return;

    form.dataset.requestQuoteBound = 'true';
    form.addEventListener('cf:form_submitted', () => this.syncSuccessState());
    form.addEventListener('cf:form_submission_failed', () => this.exitSuccessState());
    form.addEventListener('cf:step_changed', () => this.syncSuccessState());
  }

  bindFormHostClicks() {
    this.formHost.addEventListener('click', (event) => {
      const resetBtn = event.target.closest('[data-request-quote-reset], button, a');
      if (!resetBtn || !this.formHost.contains(resetBtn)) return;

      const label = resetBtn.textContent?.trim().toLowerCase() ?? '';
      if (!label.includes('submit another')) return;

      event.preventDefault();
      this.resetToForm();
    });
  }

  observeSuccess() {
    if (this.successObserver) return;

    const check = () => this.syncSuccessState();

    this.successObserver = new MutationObserver(check);
    this.successObserver.observe(this.formHost, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cf-on-success-step'],
    });
    check();
  }

  syncSuccessState() {
    if (this.isSuccessStep()) {
      this.enterSuccessState();
      return;
    }

    if (this.section.classList.contains('request-quote--success')) {
      this.exitSuccessState();
    }
  }

  enterSuccessState() {
    this.section.classList.add('request-quote--success');
    this.formHost.setAttribute('data-rq-success', 'true');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  exitSuccessState() {
    this.section.classList.remove('request-quote--success');
    this.formHost.removeAttribute('data-rq-success');
  }

  resetToForm() {
    const form = this.getForm();
    const cfForm = form?.cfForm;

    const finish = () => {
      if (!this.isSuccessStep()) {
        this.exitSuccessState();
        return;
      }
      this.resetToFormByRemount();
    };

    if (typeof cfForm?.goToStep === 'function') {
      cfForm.goToStep(0, false);
      window.setTimeout(finish, 250);
      return;
    }

    const backBtn = this.formHost.querySelector('.cf-prev-step, .cf-previous-step');
    if (backBtn) {
      backBtn.click();
      window.setTimeout(finish, 250);
      return;
    }

    this.resetToFormByRemount();
  }

  resetToFormByRemount() {
    this.formHost.innerHTML = `<form data-cf-form="${this.formId}"></form>`;
    const form = this.getForm();
    if (form) delete form.dataset.requestQuoteBound;

    this.waitForForm(() => this.bindFormEvents());
    this.ensureFormStyles();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

customElements.define('request-quote-section', RequestQuoteSection);

document.querySelectorAll('.request-quote').forEach((section) => {
  if (!section.querySelector('request-quote-section')) {
    section.appendChild(document.createElement('request-quote-section'));
  }
});
